import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Grid, Tooltip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Send as SendIcon, GetApp as DownloadIcon, Replay as ReplayIcon } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@mui/icons-material/Done';
import statesInverted from '@/constants/statesInverted.json';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import WarningIcon from '@material-ui/icons/Warning';
import getOrdersFilesAsPDF from '../../../utilities/getOrdersFilesAsPDF';
import { hasAnySubmitted210, allSuccessful210 } from '@/utilities/orderful';
import { saveAs } from 'file-saver';
import * as Sentry from '@sentry/react';
import { genDestinationAttributes, genOriginAttributes, genInvoiceColumns } from '@onward-delivery/core';
import {
    calcAccessorials,
    calcAccessorialsPerspective,
    calcIsManual,
    calcSubtotal,
    calcSubtotalPerspective,
    calcTotal,
    formatOrderIdentifier,
    hasShipperMiddleMile,
    hasShipperWarehouse,
    invoiceApprovedEvent,
} from './utils';

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

export const integerFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const useTableColumns = ({ invoice, callbacks, canViewIntegrations, reemitInFlight }) => {
    return useMemo(() => {
        if (!invoice) return [];
        return [
            {
                Header: 'Order Details',
                id: 'order-details-group',
                columns: [
                    {
                        Header: 'Order',
                        id: 'order_number',
                        width: 200,
                        accessor: ({ order }) => formatOrderIdentifier(order),
                    },
                    {
                        Header: 'Delivered',
                        id: 'delivery_date',
                        width: 125,
                        Cell: ({ row }) => {
                            const { order } = row.original;
                            return order.delivery_date_formatted || '--';
                        },
                    },
                    {
                        Header: 'Completed',
                        id: 'completed_date',
                        width: 125,
                        Cell: ({ row }) => {
                            const { order } = row.original;
                            return order.billing_completion_time_formatted || '--';
                        },
                    },
                    {
                        Header: 'POD',
                        id: 'pod',
                        width: 110,
                        Cell: ({ row }) => {
                            const { order } = row.original;
                            const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                            const hasSignature = order.customer_signature?.length >= 1;

                            const handleDownload = async (e) => {
                                e.stopPropagation();
                                try {
                                    const pdfDoc = await getOrdersFilesAsPDF(order, 'pod', []);
                                    if (pdfDoc) {
                                        saveAs(
                                            await pdfDoc.saveAsBase64({ dataUri: true }),
                                            order.order_number + ' - POD.pdf'
                                        );
                                    }
                                } catch (error) {
                                    Sentry.captureException(error);
                                    console.error(error);
                                }
                            };

                            return hasSignature || hasPhoto ? (
                                <Grid
                                    container
                                    css={css`
                                        align-items: center;
                                        justify-content: space-between;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <Grid item>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                callbacks.viewOrderPod(order);
                                            }}
                                            css={css`
                                                color: ${colors.greens.primary};
                                                text-decoration: underline;
                                                margin: 5px;
                                                font-weight: 500;
                                                background: none;
                                                border: none;
                                                cursor: pointer;
                                            `}
                                        >
                                            View
                                        </button>
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            size="small"
                                            onClick={handleDownload}
                                            css={css`
                                                padding: 4px;
                                            `}
                                        >
                                            <DownloadIcon
                                                css={css`
                                                    color: ${colors.greens.primary};
                                                `}
                                            />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ) : (
                                <CloseIcon
                                    css={css`
                                        color: ${colors.reds.primary};
                                        background: none;
                                    `}
                                />
                            );
                        },
                    },
                    {
                        Header: 'Issues',
                        id: 'issues',
                        width: 75,
                        accessor: ({ order }) => {
                            return (
                                order.exceptions.filter((mapping) => mapping.exception.status !== 'RESOLVED').length > 0
                            );
                        },
                        Cell: ({ value }) => {
                            if (value) {
                                return (
                                    <WarningIcon
                                        css={css`
                                            color: ${colors.reds.primary};
                                        `}
                                    />
                                );
                            }

                            return '--';
                        },
                    },
                    {
                        Header: 'Customer',
                        id: 'dropoff_name',
                        width: 200,
                        accessor: ({ order }) => order.dropoff_name,
                    },
                    {
                        Header: 'Origin',
                        id: 'origin',
                        width: 200,
                        accessor: ({ order }) => {
                            const {
                                city: cityAttr,
                                state: stateAttr,
                                zip: zipAttr,
                            } = genOriginAttributes(order, false, true);
                            const state = statesInverted[order[stateAttr]] || order[stateAttr];
                            return `${order[cityAttr]}, ${state} ${order[zipAttr]}`;
                        },
                    },
                    {
                        Header: 'Destination',
                        id: 'destination',
                        width: 200,
                        accessor: ({ order }) => {
                            const {
                                city: cityAttr,
                                state: stateAttr,
                                zip: zipAttr,
                            } = genDestinationAttributes(order, false, true);
                            const state = statesInverted[order[stateAttr]] || order[stateAttr];
                            return `${order[cityAttr]}, ${state} ${order[zipAttr]}`;
                        },
                        Cell: ({ value, row }) => {
                            if (row.original.order.dropoff_address_confirmed) {
                                return (
                                    <Grid
                                        container
                                        css={css`
                                            align-items: center;
                                            flex-wrap: nowrap;
                                        `}
                                    >
                                        <Grid
                                            item
                                            css={css`
                                                margin-right: 4px;
                                            `}
                                        >
                                            <Tooltip arrow placement="top" title={`Customer confirmed`}>
                                                <CheckCircleIcon
                                                    css={css`
                                                        color: ${colors.greens.primary};
                                                    `}
                                                />
                                            </Tooltip>
                                        </Grid>
                                        {value}
                                    </Grid>
                                );
                            } else {
                                return value;
                            }
                        },
                    },
                    ...(['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type)
                        ? [
                              {
                                  Header: 'FM Subtotal',
                                  id: 'fm-subtotal',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order } = row.original;
                                      const value = calcSubtotalPerspective(order, 'shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'FM Accessorials',
                                  id: 'fm-accessorials',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order } = row.original;
                                      const value = calcAccessorialsPerspective(order, 'shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'FM Total',
                                  id: 'fm-total',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order } = row.original;
                                      const total = order.shipper_rate || 0;
                                      return total ? dollarFormatter.format(total) : '--';
                                  },
                              },
                              ...(hasShipperMiddleMile(invoice)
                                  ? [
                                        {
                                            Header: 'MM Subtotal',
                                            id: 'mm-subtotal',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const value = calcSubtotalPerspective(order, 'mm_shipper');
                                                return value ? dollarFormatter.format(value) : '--';
                                            },
                                        },
                                        {
                                            Header: 'MM Accessorials',
                                            id: 'mm-accessorials',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const value = calcAccessorialsPerspective(order, 'mm_shipper');
                                                return value ? dollarFormatter.format(value) : '--';
                                            },
                                        },
                                        {
                                            Header: 'MM Total',
                                            id: 'mm-total',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const total = order.middle_mile_shipper_rate || 0;
                                                return total ? dollarFormatter.format(total) : '--';
                                            },
                                        },
                                    ]
                                  : []),
                              ...(hasShipperWarehouse(invoice)
                                  ? [
                                        {
                                            Header: 'WH Subtotal',
                                            id: 'wh-subtotal',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const value = calcSubtotalPerspective(order, 'wh_shipper');
                                                return value ? dollarFormatter.format(value) : '--';
                                            },
                                        },
                                        {
                                            Header: 'WH Accessorials',
                                            id: 'wh-accessorials',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const value = calcAccessorialsPerspective(order, 'wh_shipper');
                                                return value ? dollarFormatter.format(value) : '--';
                                            },
                                        },
                                        {
                                            Header: 'WH Total',
                                            id: 'wh-total',
                                            disableSortBy: true,
                                            width: 125,
                                            Cell: ({ row }) => {
                                                const { order } = row.original;
                                                const total = order.warehouse_shipper_rate || 0;
                                                return total ? dollarFormatter.format(total) : '--';
                                            },
                                        },
                                    ]
                                  : []),
                          ]
                        : [
                              {
                                  Header: 'Subtotal',
                                  id: 'subtotal',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order, perspective } = row.original;
                                      const value = calcSubtotal(invoice, order, perspective);
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Accessorials',
                                  id: 'accessorials',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order, perspective } = row.original;
                                      const value = calcAccessorials(invoice, order, perspective);
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Total',
                                  id: 'total',
                                  disableSortBy: true,
                                  width: 125,
                                  Cell: ({ row }) => {
                                      const { order, perspective } = row.original;
                                      const total = calcTotal(invoice, order, perspective);
                                      return dollarFormatter.format(total);
                                  },
                              },
                          ]),
                    {
                        Header: 'Manual Adj',
                        id: 'manual_adj',
                        disableSortBy: true,
                        width: 125,
                        Cell: ({ row }) => {
                            const { order, perspective } = row.original;
                            const isManual = calcIsManual(invoice, order, perspective);
                            return isManual ? 'Yes' : '--';
                        },
                    },
                    ...(canViewIntegrations
                        ? [
                              {
                                  Header: 'Orderful',
                                  id: 'orderful',
                                  width: 90,
                                  disableSortBy: true,
                                  Cell: ({ row }) => {
                                      const { order } = row.original;
                                      if (!hasAnySubmitted210(invoice, [order])) {
                                          return;
                                      }

                                      return allSuccessful210(invoice, [order]) ? (
                                          <DoneIcon
                                              css={css`
                                                  color: ${colors.greens.primary};
                                              `}
                                          />
                                      ) : (
                                          <CloseIcon
                                              css={css`
                                                  color: ${colors.reds.primary};
                                                  background: none;
                                              `}
                                          />
                                      );
                                  },
                              },
                          ]
                        : []),
                ],
            },
            {
                sticky: 'right',
                Header: 'Sticky',
                id: 'sticky-group',
                columns: [
                    {
                        Header: 'Details',
                        id: 'details',
                        width: 80,
                        Cell: ({ row, callbacks }) => (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    callbacks.gotoDetails(row.original.order);
                                }}
                                css={css`
                                    padding: 8px;
                                `}
                            >
                                <SendIcon
                                    css={css`
                                        color: ${colors.greens.primary};
                                    `}
                                />
                            </IconButton>
                        ),
                        disableSortBy: true,
                    },

                    ...(canViewIntegrations
                        ? [
                              {
                                  Header: 'Re-emit',
                                  id: 'reemit-approved',
                                  width: 80,
                                  disableSortBy: true,
                                  Cell: ({ row }) => {
                                      const { order, perspective } = row.original;
                                      const event = invoiceApprovedEvent(invoice, perspective);
                                      if (!event) return null;

                                      return (
                                          <Tooltip arrow placement="top" title={`Re-send`}>
                                              {/* span keeps the tooltip working while the button is disabled */}
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      disabled={
                                                          allSuccessful210(invoice, [order]) ||
                                                          !!reemitInFlight?.[row.id]
                                                      }
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          callbacks.reemitApproved(row.original);
                                                      }}
                                                      css={css`
                                                          padding: 8px;

                                                          color: ${colors.greens.primary};
                                                      `}
                                                  >
                                                      <ReplayIcon />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      );
                                  },
                              },
                          ]
                        : []),

                    ...(['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type)
                        ? [
                              {
                                  Header: 'Shipper',
                                  id: 'edit-shipper',
                                  width: 80,
                                  Cell: ({ row, callbacks }) => {
                                      const { order } = row.original;

                                      return (
                                          <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.onEdit(order, 'shipper');
                                              }}
                                              css={css`
                                                  padding: 8px;

                                                  color: ${colors.greens.primary};
                                              `}
                                          >
                                              <EditIcon />
                                          </IconButton>
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type) && hasShipperMiddleMile(invoice)
                        ? [
                              {
                                  Header: 'MM Shipper',
                                  id: 'edit-mm-shipper',
                                  width: 80,
                                  Cell: ({ row, callbacks }) => {
                                      const { order } = row.original;

                                      return (
                                          <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.onEdit(order, 'mm_shipper');
                                              }}
                                              css={css`
                                                  padding: 8px;

                                                  color: ${colors.greens.primary};
                                              `}
                                          >
                                              <EditIcon />
                                          </IconButton>
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type) && hasShipperWarehouse(invoice)
                        ? [
                              {
                                  Header: 'WH Shipper',
                                  id: 'edit-wh-shipper',
                                  width: 80,
                                  Cell: ({ row, callbacks }) => {
                                      const { order } = row.original;

                                      return (
                                          <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.onEdit(order, 'wh_shipper');
                                              }}
                                              css={css`
                                                  padding: 8px;

                                                  color: ${colors.greens.primary};
                                              `}
                                          >
                                              <EditIcon />
                                          </IconButton>
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(['PAYABLE', 'ONWARD_PAYABLE'].includes(invoice.type)
                        ? [
                              {
                                  Header: 'Carrier',
                                  id: 'edit-carrier',
                                  width: 80,
                                  Cell: ({ row, callbacks }) => {
                                      const { order, perspective } = row.original;
                                      return (
                                          <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.onEdit(order, perspective);
                                              }}
                                              css={css`
                                                  padding: 8px;

                                                  color: ${colors.greens.primary};
                                              `}
                                          >
                                              <EditIcon />
                                          </IconButton>
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(['INTERNAL'].includes(invoice.type)
                        ? [
                              {
                                  Header: 'Internal',
                                  id: 'edit-internal',
                                  width: 80,
                                  Cell: ({ row, callbacks }) => {
                                      const { order } = row.original;
                                      return (
                                          <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.onEdit(order, 'internal');
                                              }}
                                              css={css`
                                                  padding: 8px;

                                                  color: ${colors.greens.primary};
                                              `}
                                          >
                                              <EditIcon />
                                          </IconButton>
                                      );
                                  },
                              },
                          ]
                        : []),
                ],
            },
        ];
    }, [invoice, callbacks, canViewIntegrations, reemitInFlight]);
};

export const useQuickbooksColumns = ({ invoice }) => {
    return useMemo(() => genInvoiceColumns({ invoice }), [invoice]);
};
